import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Play, RotateCw, ShieldCheck, Image as ImageIcon, Filter, Settings } from "lucide-react";
import { getStoreId } from "../utils/storage";
import { getAuthData } from "../utils/auth";
import * as logger from "../utils/logger";
import { showToast } from "../utils/toast";

const DEFAULT_SETTINGS = {
  cruise_control_enabled: false,
  compression_enabled: false,
  compression_quality: "medium",
  jpeg_quality: 65,
  png_quality: 65,
  png_to_jpeg_enabled: false,
  convert_to_webp_enabled: false,
  backup_retention_days: 3
};

const ImageOptimizer = ({ initialTab }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab || "images");

  useEffect(() => {
    if (initialTab != null && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const [creditsAvailable, setCreditsAvailable] = useState(0);
  const [imageOptimizerQuota, setImageOptimizerQuota] = useState({ used: 0, limit: 0, remaining: 0, unlimited: true });

  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [imagesError, setImagesError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [optimizedFilter, setOptimizedFilter] = useState("");
  /** Ecwid enabled products only (storefront-visible) */
  const [productListingFilter, setProductListingFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const [queue, setQueue] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(50);

  const storeId = getStoreId();
  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hasNoQuota = !imageOptimizerQuota.unlimited && (imageOptimizerQuota.remaining || 0) <= 0;
  const canOptimize = !hasNoQuota;

  const fetchSettingsInFlightRef = useRef(false);
  const fetchCategoriesInFlightRef = useRef(false);
  const fetchImagesQueueInFlightRef = useRef(false);
  const fetchHistoryInFlightRef = useRef(false);
  const optimizeButtonRef = useRef(null);

  useEffect(() => {
    if (fetchSettingsInFlightRef.current) return;
    fetchSettingsInFlightRef.current = true;
    fetchSettings().finally(() => { fetchSettingsInFlightRef.current = false; });
  }, []);

  useEffect(() => {
    if (activeTab !== "images" || !storeId) return;
    if (fetchCategoriesInFlightRef.current) return;
    fetchCategoriesInFlightRef.current = true;
    fetchCategories().finally(() => { fetchCategoriesInFlightRef.current = false; });
  }, [activeTab, storeId]);

  useEffect(() => {
    if (activeTab !== "images") return;
    if (fetchImagesQueueInFlightRef.current) return;
    fetchImagesQueueInFlightRef.current = true;
    Promise.all([fetchImages(), fetchQueue()]).finally(() => { fetchImagesQueueInFlightRef.current = false; });
  }, [activeTab, page, limit, searchFilter, categoryFilter, optimizedFilter, productListingFilter]);

  useEffect(() => {
    if (activeTab !== "history") return;
    if (fetchHistoryInFlightRef.current) return;
    fetchHistoryInFlightRef.current = true;
    fetchHistory().finally(() => { fetchHistoryInFlightRef.current = false; });
  }, [activeTab, historyPage, historyLimit]);

  const fetchSettings = async () => {
    if (!storeId) return;
    setSettingsLoading(true);
    try {
      const settingsResponse = await fetch(`${apiUrl}/api/image-optimizer/settings?id=${storeId}`);
      const data = await settingsResponse.json();
      const merged = { ...DEFAULT_SETTINGS, ...data };
      merged.cruise_control_enabled = merged.cruise_control_enabled === true;
      setSettings(merged);

      const appSettingsResponse = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
      const appSettingsData = await appSettingsResponse.json();
      const appSettings = appSettingsData?.settings || {};
      setCreditsAvailable(appSettings.credits_available ?? 0);
      setImageOptimizerQuota(appSettings.image_optimizer_quota || { used: 0, limit: 0, remaining: 0, unlimited: true });
    } catch (error) {
      showToast(t("imageOptimizer.failedToLoadSettings"), "error");
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!storeId) return;
    setCategoriesLoading(true);
    try {
      const allCategories = [];
      let offset = 0;
      const batchLimit = 100;
      let total = null;
      let hasMore = true;
      
      const auth = getAuthData();
      while (hasMore) {
        let categoryUrl = `${apiUrl}/api/seo-optimizer/categories?id=${storeId}&limit=${batchLimit}&offset=${offset}`;
        if (auth?.access_token) categoryUrl += `&token=${encodeURIComponent(auth.access_token)}`;
        const response = await fetch(categoryUrl);
        
        if (!response.ok) {
          logger.error('Failed to fetch categories:', response.status, response.statusText);
          hasMore = false;
          break;
        }
        
        const data = await response.json();
        
        if (data.success !== false && data.categories) {
          if (total === null) {
            total = data.total || 0;
          }
          
          if (data.categories.length > 0) {
            allCategories.push(...data.categories);
            offset += batchLimit;
            hasMore = data.categories.length === batchLimit && allCategories.length < total;
          } else {
            hasMore = false;
          }
        } else {
          logger.error('Invalid categories response:', data);
          hasMore = false;
        }
      }
      
      setCategories(allCategories);
    } catch (error) {
      logger.error('Failed to fetch categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchImages = async () => {
    if (!storeId) return;
    setImagesLoading(true);
    setImagesError(null);
    try {
      const params = new URLSearchParams({
        id: storeId,
        page: page.toString(),
        limit: limit.toString()
      });
      if (searchFilter) params.set("search", searchFilter);
      if (categoryFilter) params.set("category", categoryFilter);
      if (optimizedFilter) params.set("optimized", optimizedFilter);
      if (productListingFilter === "online") params.set("onlineOnly", "true");
      const auth = getAuthData();
      if (auth?.access_token) params.set("token", auth.access_token);

      const response = await fetch(`${apiUrl}/api/image-optimizer/images?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) {
        setImagesError(data?.error || t("imageOptimizer.failedToLoadImages"));
        showToast(data?.error || t("imageOptimizer.failedToLoadImages"), "error");
        setImages([]);
        setTotal(0);
        return;
      }
      setImages(data.images || []);
      setTotal(data.total || 0);
      
      // Track initial load completion
      if (!hasInitialLoad) {
        setHasInitialLoad(true);
      }
    } catch (error) {
      setImagesError(t("imageOptimizer.failedToLoadImages"));
      showToast(t("imageOptimizer.failedToLoadImages"), "error");
    } finally {
      setImagesLoading(false);
    }
  };

  const fetchQueue = async () => {
    if (!storeId) return;
    setQueueLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/image-optimizer/queue?id=${storeId}`);
      const data = await response.json();
      setQueue(data);
    } catch (error) {
      setQueue(null);
    } finally {
      setQueueLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!storeId) return;
    setHistoryLoading(true);
    try {
      const offset = (historyPage - 1) * historyLimit;
      const response = await fetch(`${apiUrl}/api/image-optimizer/history?id=${storeId}&limit=${historyLimit}&offset=${offset}`);
      const data = await response.json();
      setHistory(data.history || []);
    } catch (error) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOptimize = async (imageIdsOverride, operationsOverride) => {
    const toastOpts = { anchor: optimizeButtonRef, anchorPosition: "above" };
    if (!canOptimize) {
      showToast(t("imageOptimizer.quotaExceeded"), "error", 6000, toastOpts);
      return;
    }

    const targets = imageIdsOverride?.length
      ? imageIdsOverride
      : images.filter((img) => selectedImages.includes(`${img.product_id}_${img.image_id}`));

    if (!targets.length) {
      showToast(t("imageOptimizer.selectOneImage"), "error", 6000, toastOpts);
      return;
    }

    setIsOptimizing(true);

    try {
      const payload = {
        image_ids: targets.map((img) => ({
          product_id: img.product_id,
          image_id: img.image_id,
          image_url: img.image_url
        })),
        operations: operationsOverride || ["compress"]
      };

      const response = await fetch(`${apiUrl}/api/image-optimizer/bulk-optimize?id=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t("imageOptimizer.failedToOptimize"));
      }

      showToast(t("imageOptimizer.imagesAddedToQueue", { count: payload.image_ids.length }), "success", 4000, toastOpts);
      setSelectedImages([]);
      await fetchQueue();
      await fetchImages();
      await fetchSettings();
    } catch (error) {
      showToast(error.message || t("imageOptimizer.failedToOptimize"), "error", 6000, toastOpts);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleProcessQueue = async () => {
    if (!storeId) return;
    setProcessingQueue(true);
    try {
      await fetch(`${apiUrl}/api/image-optimizer/process-queue?id=${storeId}`, { method: "POST" });
      fetchQueue();
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleRestore = async (backupId, event) => {
    if (!storeId) return;
    const toastOpts = event?.currentTarget
      ? { anchor: event.currentTarget, anchorPosition: "above" }
      : {};
    try {
      await fetch(`${apiUrl}/api/image-optimizer/restore?id=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ backup_id: backupId })
      });
      // Optimistic update: hide Restore button by clearing backup for this image
      setImages((prev) =>
        prev.map((img) => {
          const hasThisBackup = img.backups?.some((b) => b.id === backupId);
          if (!hasThisBackup) return img;
          return {
            ...img,
            is_optimized: false,
            backups_count: 0,
            backups: []
          };
        })
      );
      fetchImages();
      showToast(t("imageOptimizer.imageRestored"), "success", 4000, toastOpts);
    } catch (error) {
      showToast(t("imageOptimizer.failedToRestore"), "error", 6000, toastOpts);
    }
  };

  const handleSaveSettings = async () => {
    if (!storeId) return;
    setSavingSettings(true);
    try {
      const response = await fetch(`${apiUrl}/api/image-optimizer/settings?id=${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t("imageOptimizer.failedToSaveSettings"));
      setSettings(data);
      showToast(t("imageOptimizer.settingsSaved"), "success");
    } catch (error) {
      showToast(error.message || t("imageOptimizer.failedToSaveSettings"), "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const subTabLabels = { images: t("imageOptimizer.imagesTab"), settings: t("imageOptimizer.settingsTab"), history: t("imageOptimizer.historyTab") };
  const pageTitle = `Image Optimizer → ${subTabLabels[activeTab] ?? subTabLabels.images}`;

  return (
    <div className="container-fluid py-1">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="fw-bold mb-0">{pageTitle}</h3>
      </div>

      {activeTab === "images" && (
        <>
          {/* Info: What Image Optimizer does */}
          <div className="card border mb-3 border-start border-2 rounded-end" style={{ backgroundColor: '#f8f9fa', borderLeftColor: '#adb5bd' }}>
            <div className="card-body py-2 px-3">
              <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
                <li className="fw-semibold mb-1"><span className="text-dark">{t("imageOptimizer.infoCard1Label")}</span> {t("imageOptimizer.infoCard1Desc")}</li>
                <li className="fw-semibold mb-1"><span className="text-dark">{t("imageOptimizer.infoCard2Label")}</span> {t("imageOptimizer.infoCard2Desc")}</li>
                <li className="fw-semibold"><span className="text-dark">{t("imageOptimizer.infoCard3Label")}</span> {t("imageOptimizer.infoCard3Desc")}</li>
              </ul>
            </div>
          </div>

          {!settingsLoading && hasNoQuota && (
            <div className="alert alert-warning mb-3 d-flex align-items-center" role="alert">
              <span className="fw-semibold me-2">{t("imageOptimizer.quotaExceeded")}</span>
              {!imageOptimizerQuota.unlimited && (
                <span className="small">
                  {t("imageOptimizer.usageUsed", { used: imageOptimizerQuota.used, limit: imageOptimizerQuota.limit })}
                  {" — "}
                  <a href="#upgrade" className="alert-link">{t("dashboard.upgradeNow")}</a>
                </span>
              )}
            </div>
          )}
          <div className="card mb-3 border-0 shadow-sm" style={{ background: "#f8f9fb" }}>
            <div className="card-body py-2 px-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h6 className="fw-semibold mb-0" style={{ fontSize: "0.95rem" }}>{t("imageOptimizer.imageOptimization")}</h6>
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>{t("imageOptimizer.imageOptimizationDesc")}</span>
                </div>
                {!settingsLoading && (
                  <div className="d-flex align-items-center gap-3 small text-muted" style={{ fontSize: "0.8rem" }}>
                    <span title={t("imageOptimizer.usageTooltip")}>
                      {imageOptimizerQuota.unlimited
                        ? t("imageOptimizer.usageUnlimited")
                        : t("imageOptimizer.usageUsed", { used: imageOptimizerQuota.used, limit: imageOptimizerQuota.limit })}
                    </span>
                  </div>
                )}
                <div className="d-flex flex-wrap gap-1">
                  <span className="badge bg-light text-dark border" style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>{t("imageOptimizer.smartCompression")}</span>
                  <span className="badge bg-light text-dark border" style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>{t("imageOptimizer.pngWebpToJpg")}</span>
                  <span className="badge bg-light text-dark border" style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>{t("imageOptimizer.autoOptimize")}</span>
                  <span className="badge bg-light text-dark border" style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>{t("imageOptimizer.backupRestore")}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3 border-0 shadow-sm">
            <div className="card-body py-2 px-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="d-flex align-items-center gap-1 text-muted small fw-semibold me-1">
                  <Filter size={14} strokeWidth={2.5} />
                  {t("imageOptimizer.filters")}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ width: "clamp(120px, 18vw, 200px)" }}
                  placeholder={t("imageOptimizer.searchPlaceholder")}
                  value={searchFilter}
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                    setPage(1);
                  }}
                />
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: "140px" }}
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                  disabled={categoriesLoading}
                >
                  <option value="">{t("imageOptimizer.allCategories")}</option>
                  {categoriesLoading ? (
                    <option disabled>{t("common.loading")}</option>
                  ) : (
                    categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))
                  )}
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: "110px" }}
                  value={productListingFilter}
                  onChange={(e) => {
                    setProductListingFilter(e.target.value);
                    setPage(1);
                  }}
                  aria-label={t("seoOptimizer.productListingFilter")}
                >
                  <option value="all">{t("seoOptimizer.listingAll")}</option>
                  <option value="online">{t("seoOptimizer.listingOnlineOnly")}</option>
                </select>
                <span className="d-flex align-items-center gap-1 text-muted small fw-semibold ms-2 me-1" style={{ fontSize: "0.8rem" }}>
                  {t("imageOptimizer.optimizeStatus")}
                </span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: "140px" }}
                  value={optimizedFilter}
                  onChange={(e) => {
                    setOptimizedFilter(e.target.value);
                    setPage(1);
                  }}
                  aria-label={t("imageOptimizer.filterByStatus")}
                >
                  <option value="">{t("imageOptimizer.all")}</option>
                  <option value="true">{t("imageOptimizer.optimized")}</option>
                  <option value="false">{t("imageOptimizer.notOptimized")}</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: "70px" }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-muted small ms-1">{t("imageOptimizer.perPage")}</span>
                <div className="ms-auto d-flex align-items-center gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    onClick={() => {
                      fetchImages();
                      fetchQueue();
                    }}
                    disabled={imagesLoading}
                    title={t("imageOptimizer.refreshTable")}
                  >
                    <RefreshCw size={14} style={imagesLoading ? { animation: "spin 0.8s linear infinite" } : undefined} />
                    {t("imageOptimizer.refresh")}
                  </button>
                  <button
                    ref={optimizeButtonRef}
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOptimize()}
                    disabled={selectedImages.length === 0 || isOptimizing || !canOptimize}
                  >
                    {t("imageOptimizer.optimizeCount", { count: selectedImages.length })}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="card mb-3 border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}>
            <div className="card-body py-2 px-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h6 className="fw-semibold mb-0 small" style={{ fontSize: "0.9rem" }}>{t("imageOptimizer.optimizationQueue")}</h6>
                {queueLoading ? (
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>{t("common.loading")}</span>
                ) : (
                  <>
                    <span className="badge" style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem", backgroundColor: "rgba(245, 158, 11, 0.2)", color: "#b45309", border: "1px solid rgba(245, 158, 11, 0.4)" }}>{t("imageOptimizer.pending")}: {queue?.status_counts?.PENDING || 0}</span>
                    <span className="badge" style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem", backgroundColor: "rgba(59, 130, 246, 0.2)", color: "#1d4ed8", border: "1px solid rgba(59, 130, 246, 0.4)" }}>{t("imageOptimizer.processing")}: {queue?.status_counts?.PROCESSING || 0}</span>
                    <span className="badge" style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem", backgroundColor: "rgba(34, 197, 94, 0.2)", color: "#15803d", border: "1px solid rgba(34, 197, 94, 0.4)" }}>{t("imageOptimizer.completed")}: {queue?.status_counts?.COMPLETED || 0}</span>
                    <span className="badge" style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem", backgroundColor: "rgba(239, 68, 68, 0.15)", color: "#b91c1c", border: "1px solid rgba(239, 68, 68, 0.35)" }}>{t("imageOptimizer.failed")}: {queue?.status_counts?.FAILED || 0}</span>
                  </>
                )}
                <div className="d-flex gap-1 ms-auto">
                  <button className="btn btn-outline-secondary btn-sm py-0 px-1" style={{ fontSize: "0.75rem" }} onClick={fetchQueue} title={t("imageOptimizer.refresh")}>
                    <RefreshCw size={12} />
                  </button>
                  <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: "0.75rem" }} onClick={handleProcessQueue} disabled={processingQueue}>
                    <Play size={12} className="me-0" />
                    {processingQueue ? "..." : t("imageOptimizer.process")}
                  </button>
                </div>
              </div>
            </div>
          </div> */}

          <div className="card border-0 shadow-sm">
            <div className="card-body position-relative">
              
              {/* Loading overlay - only show when filters change (after initial load) */}
              {hasInitialLoad && imagesLoading && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'rgba(255, 255, 255, 0.9)', zIndex: 10, borderRadius: '0.375rem' }}>
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">{t("common.loading")}</span>
                    </div>
                    <div className="mt-2 small text-muted">{t("imageOptimizer.refreshing")}</div>
                  </div>
                </div>
              )}
              
              {!hasInitialLoad && imagesLoading ? (
                // Show full loading only on initial load
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t("imageOptimizer.loadingImages")}</span>
                  </div>
                  <div className="mt-2 text-muted">{t("imageOptimizer.loadingImages")}</div>
                </div>
              ) : images && images.length > 0 ? (
                <>
                  <div 
                    className="table-responsive" 
                    style={{ 
                      maxHeight: '600px', 
                      overflowY: 'auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem'
                    }}
                  >
                    <table className="table table-hover mb-0">
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                        <tr>
                          <th style={{ backgroundColor: '#fff' }}>
                            <input
                              type="checkbox"
                              checked={selectedImages.length > 0 && selectedImages.length === images.length && images.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedImages(images.map((img) => `${img.product_id}_${img.image_id}`));
                                } else {
                                  setSelectedImages([]);
                                }
                              }}
                            />
                          </th>
                          <th style={{ backgroundColor: '#fff' }}>{t("imageOptimizer.thumbnail")}</th>
                          <th style={{ backgroundColor: '#fff' }}>{t("imageOptimizer.productFile")}</th>
                          <th style={{ backgroundColor: '#fff' }}>{t("imageOptimizer.status")}</th>
                          <th style={{ backgroundColor: '#fff' }}>{t("imageOptimizer.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {images.map((img) => {
                          const key = `${img.product_id}_${img.image_id}`;
                          return (
                            <tr key={key}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedImages.includes(key)}
                                  onChange={() => {
                                    setSelectedImages((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
                                  }}
                                />
                              </td>
                              <td>
                                {img.image_url ? (
                                  <img
                                    src={img.image_url}
                                    alt={img.product_name}
                                    style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                    className="rounded"
                                  />
                                ) : (
                                  <ImageIcon size={24} />
                                )}
                              </td>
                              <td>
                                <div className="fw-semibold">{img.product_name}</div>
                                <div className="small text-muted">{img.file_name}</div>
                              </td>
                              <td>
                                {img.is_optimized ? (
                                  <div>
                                    <span className="badge bg-success text-white mb-1">{t("imageOptimizer.optimized")}</span>
                                    {img.optimization_info && img.optimization_info.compression_ratio !== null && 
                                     img.optimization_info.compression_ratio !== undefined && 
                                     typeof img.optimization_info.compression_ratio === 'number' && (
                                      <div className="small text-muted">{Number(img.optimization_info.compression_ratio).toFixed(1)}% smaller</div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="badge bg-light text-dark border">{t("imageOptimizer.notOptimized")}</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleOptimize([img], ['compress', 'rename'])}
                                    disabled={isOptimizing || !canOptimize}
                                  >
                                    {t("imageOptimizer.optimize")}
                                  </button>
                                  {img.is_optimized && img.backups_count > 0 && (
                                    <button className="btn btn-sm btn-outline-secondary" onClick={(e) => handleRestore(img.backups?.[0]?.id, e)}>
                                      {t("imageOptimizer.restore")}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      {t("imageOptimizer.showingXtoYofZ", {
                        from: ((page - 1) * limit) + 1,
                        to: Math.min(page * limit, total || 0),
                        total: total || 0
                      })}
                    </div>
                    <div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        {t("imageOptimizer.previous")}
                      </button>
                      <span className="mx-2">{t("imageOptimizer.page")} {page}</span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setPage(p => p + 1)}
                        disabled={!total || page * limit >= total}
                      >
                        {t("imageOptimizer.next")}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center p-4 text-muted">
                  {t("imageOptimizer.noImagesFound")}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === "settings" && (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-3 p-md-4">
            {settingsLoading ? (
              <p className="text-muted mb-0">{t("imageOptimizer.loadingSettings")}</p>
            ) : (
              <>
                {/* Toggles: switch right next to label (compact) */}
                <div className="d-flex flex-column gap-3 mb-4">
                  <div className="d-flex align-items-start gap-3 py-2 border-bottom">
                    <div className="form-check form-switch mb-0 flex-shrink-0" style={{ marginTop: "2px" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="setting-auto-optimize"
                        checked={settings.cruise_control_enabled}
                        onChange={(e) => setSettings({ ...settings, cruise_control_enabled: e.target.checked })}
                      />
                    </div>
                    <div>
                      <label className="fw-semibold mb-0 cursor-pointer" htmlFor="setting-auto-optimize" style={{ fontSize: "0.95rem" }}>
                        {t("imageOptimizer.autoOptimizeNew")}
                      </label>
                      <p className="text-muted small mb-0 mt-1" style={{ fontSize: "0.8rem" }}>{t("imageOptimizer.processNewUploads")}</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-3 py-2 border-bottom">
                    <div className="form-check form-switch mb-0 flex-shrink-0" style={{ marginTop: "2px" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="setting-compression"
                        checked={settings.compression_enabled}
                        onChange={(e) => setSettings({ ...settings, compression_enabled: e.target.checked })}
                      />
                    </div>
                    <div>
                      <label className="fw-semibold mb-0 cursor-pointer" htmlFor="setting-compression" style={{ fontSize: "0.95rem" }}>
                        {t("imageOptimizer.enableCompression")}
                      </label>
                      <p className="text-muted small mb-0 mt-1" style={{ fontSize: "0.8rem" }}>{t("imageOptimizer.reduceFileSize")}</p>
                    </div>
                  </div>
                </div>

                {settings.compression_enabled && (
                  <div className="bg-light rounded-3 p-3 mb-4">
                    <p className="fw-semibold small mb-2" style={{ fontSize: "0.9rem" }}>{t("imageOptimizer.quality")}</p>
                    <p className="text-muted small mb-3" style={{ fontSize: "0.8rem" }}>{t("imageOptimizer.qualityHelp")}</p>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {["high", "medium", "low", "custom"].map((q) => (
                        <button
                          key={q}
                          type="button"
                          className={`btn btn-sm ${settings.compression_quality === q ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setSettings({ ...settings, compression_quality: q })}
                        >
                          {q === "custom" ? t("imageOptimizer.custom") : t(`imageOptimizer.${q}`)}
                        </button>
                      ))}
                    </div>
                    {["high", "medium", "low"].includes(settings.compression_quality) && (
                      <p className="text-muted small mb-3 mb-md-0" style={{ fontSize: "0.8rem" }}>
                        {t("imageOptimizer.compressionPercent", {
                          percent: { high: 92, medium: 85, low: 75 }[settings.compression_quality]
                        })}
                      </p>
                    )}
                    {settings.compression_quality === "custom" && (
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        <div style={{ minWidth: "100px" }}>
                          <label className="form-label small mb-1 fw-semibold">{t("imageOptimizer.jpegPercent")}</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min={1}
                            max={100}
                            value={settings.jpeg_quality}
                            onChange={(e) => setSettings({ ...settings, jpeg_quality: Number(e.target.value) })}
                          />
                        </div>
                        <div style={{ minWidth: "100px" }}>
                          <label className="form-label small mb-1 fw-semibold">{t("imageOptimizer.pngPercent")}</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min={1}
                            max={100}
                            value={settings.png_quality}
                            onChange={(e) => setSettings({ ...settings, png_quality: Number(e.target.value) })}
                          />
                        </div>
                      </div>
                    )}
                    <p className="fw-semibold small mb-2 mt-3" style={{ fontSize: "0.9rem" }}>{t("imageOptimizer.format")}</p>
                    <div className="d-flex flex-wrap gap-3">
                      <label className="d-flex align-items-center gap-2 small mb-0 cursor-pointer">
                        <input
                          type="radio"
                          name="output_format"
                          checked={settings.png_to_jpeg_enabled === true && settings.convert_to_webp_enabled === false}
                          onChange={() => setSettings({ ...settings, png_to_jpeg_enabled: true, convert_to_webp_enabled: false })}
                        />
                        {t("imageOptimizer.convertToJpg")}
                      </label>
                      <label className="d-flex align-items-center gap-2 small mb-0 cursor-pointer">
                        <input
                          type="radio"
                          name="output_format"
                          checked={settings.convert_to_webp_enabled === true}
                          onChange={() => setSettings({ ...settings, convert_to_webp_enabled: true, png_to_jpeg_enabled: false })}
                        />
                        {t("imageOptimizer.convertToWebp")}
                      </label>
                      <label className="d-flex align-items-center gap-2 small mb-0 cursor-pointer">
                        <input
                          type="radio"
                          name="output_format"
                          checked={settings.png_to_jpeg_enabled === false && settings.convert_to_webp_enabled === false}
                          onChange={() => setSettings({ ...settings, png_to_jpeg_enabled: false, convert_to_webp_enabled: false })}
                        />
                        {t("imageOptimizer.keepOriginal")}
                      </label>
                    </div>
                  </div>
                )}

                {/* Backup + Save */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pt-3 border-top">
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label small mb-0 fw-semibold">{t("imageOptimizer.backupRetention")}</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: "4.5rem" }}
                      min={1}
                      value={settings.backup_retention_days}
                      onChange={(e) => setSettings({ ...settings, backup_retention_days: Number(e.target.value) })}
                    />
                    <span className="small text-muted">{t("imageOptimizer.days")}</span>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveSettings} disabled={savingSettings}>
                    {savingSettings ? t("imageOptimizer.saving") : t("imageOptimizer.saveSettings")}
                  </button>
                </div>
                <p className="text-muted small mb-0 mt-2">
                  {t("imageOptimizer.backupsKeptFor", { days: settings.backup_retention_days || 3 })}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === "history" && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {historyLoading ? (
              <p className="text-muted">{t("imageOptimizer.loadingHistory")}</p>
            ) : (
              <>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <button className="btn btn-outline-secondary btn-sm" onClick={fetchHistory}>
                    <RefreshCw size={14} className="me-1" /> {t("imageOptimizer.refresh")}
                  </button>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={historyLimit} onChange={(e) => setHistoryLimit(Number(e.target.value))}>
                    {[25, 50, 100].map((value) => (
                      <option key={value} value={value}>{value} {t("imageOptimizer.perPage")}</option>
                    ))}
                  </select>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>{t("imageOptimizer.image")}</th>
                        <th>{t("imageOptimizer.date")}</th>
                        <th>{t("imageOptimizer.productId")}</th>
                        <th>{t("imageOptimizer.format")}</th>
                        <th>{t("imageOptimizer.originalSize")}</th>
                        <th>{t("imageOptimizer.optimizedSize")}</th>
                        <th>{t("imageOptimizer.savings")}</th>
                        <th>{t("imageOptimizer.status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.original_image_url ? (
                              <img src={item.original_image_url} alt="" className="rounded" style={{ width: 48, height: 48, objectFit: "cover" }} />
                            ) : (
                              <ImageIcon size={18} />
                            )}
                          </td>
                          <td>{item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</td>
                          <td>{item.product_id}</td>
                          <td className="text-uppercase">{item.output_format || "-"}</td>
                          <td>{item.original_size ? `${Math.round(item.original_size / 1024)} KB` : "-"}</td>
                          <td>{item.optimized_size ? `${Math.round(item.optimized_size / 1024)} KB` : "-"}</td>
                          <td>
                            {item.compression_ratio !== null && item.compression_ratio !== undefined ? (
                              <span className="badge bg-success">{Number(item.compression_ratio).toFixed(1)}%</span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {(item.operation_type || '').toLowerCase() === 'restore' ? (
                              <span className="badge d-inline-flex align-items-center gap-1" style={{ backgroundColor: "rgba(59, 130, 246, 0.15)", color: "#1d4ed8", border: "1px solid rgba(59, 130, 246, 0.4)" }}>
                                <RotateCw size={12} /> {t("imageOptimizer.restored")}
                              </span>
                            ) : (
                              <span className="badge bg-light text-dark border">{item.status}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageOptimizer;
